import React from "react";
import moment from "moment";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import PlaybookBuildService from "../../services/PlaybookBuildService";
import PlaybookChangeRequestService from "../../services/PlaybookChangeRequestService";
import PlaybookEditService from "../../services/PlaybookEditService";
import UtilityService from "../../services/UtilityService";
import PlaybookChangeModal from "./PlaybookChangeModal";

import "./playbook-instructions-panel.css";

class PlaybookInstructionsPanel extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			draftText: "",
			draftNote: "",
			isAdding: false,
			addError: null,
			change: null,
			lastOutcome: null,
			changeRequests: [],
			requestsError: null,
			rejectingRequestId: null,
			rejectReason: "",
			isDeciding: false,
		};
	}

	componentDidMount() {
		this.fetchChangeRequests();
	}

	fetchChangeRequests = async () => {
		const { agent } = this.props;
		try {
			const changeRequests = await PlaybookChangeRequestService.listChangeRequests({ playbookId: agent.playbookId });
			await this.update({ changeRequests, requestsError: null });
		} catch (error) {
			await this.update({
				requestsError: UtilityService.describeRequestError({ error, fallback: "The changes that need approval could not be loaded." }),
			});
		}
	};

	showOutcome = async ({ outcome }) => {
		const { onChanged } = this.props;
		await this.update({ lastOutcome: outcome });
		onChanged({ agent: outcome.agent });
		this.fetchChangeRequests();
	};

	onAdd = async () => {
		const { agent } = this.props;
		const { draftText, draftNote } = this.state;
		await this.update({ isAdding: true, addError: null, lastOutcome: null });
		try {
			const outcome = await PlaybookEditService.addAddition({ playbookId: agent.playbookId, text: draftText.trim(), changeNote: draftNote.trim() });
			await this.update({ isAdding: false, draftText: "", draftNote: "" });
			await this.showOutcome({ outcome });
		} catch (error) {
			await this.update({ isAdding: false, addError: UtilityService.describeRequestError({ error, fallback: "The addition did not save." }) });
		}
	};

	onDecideRequest = async ({ requestId, isApproved }) => {
		const { agent, onChanged } = this.props;
		const { rejectReason } = this.state;
		await this.update({ isDeciding: true });
		try {
			const changeRequests = await PlaybookChangeRequestService.decideChangeRequest({ requestId, isApproved, reason: isApproved ? "" : rejectReason.trim() });
			await this.update({ changeRequests, isDeciding: false, rejectingRequestId: null, rejectReason: "" });
			if (isApproved) onChanged({ agent: await PlaybookEditService.getPlaybookForEditing({ playbookId: agent.playbookId }) });
		} catch (error) {
			await this.update({ isDeciding: false, addError: UtilityService.describeRequestError({ error, fallback: "That decision did not save." }) });
		}
	};

	onWithdrawRequest = async ({ requestId }) => {
		await this.update({ requestsError: null });
		try {
			const changeRequests = await PlaybookChangeRequestService.withdrawChangeRequest({ requestId });
			await this.update({ changeRequests });
		} catch (error) {
			await this.update({ requestsError: UtilityService.describeRequestError({ error, fallback: "The change was not withdrawn." }) });
		}
	};

	onOpenChange = change => {
		this.update({ change });
	};

	onCloseChange = () => {
		this.update({ change: null });
	};

	onConfirmChange = async ({ text, changeNote }) => {
		const { agent, onChanged } = this.props;
		const { change } = this.state;
		try {
			if (change.kind === "edit") {
				const outcome = await PlaybookEditService.editAddition({ playbookId: agent.playbookId, additionId: change.addition.id, text, changeNote });
				await this.update({ change: null });
				await this.showOutcome({ outcome });
				return null;
			}
			let updated = null;
			if (change.kind === "remove")
				updated = await PlaybookEditService.removeAddition({ playbookId: agent.playbookId, additionId: change.addition.id, changeNote });
			if (change.kind === "base") updated = await PlaybookEditService.editBase({ playbookId: agent.playbookId, baseInstructions: text, changeNote });
			await this.update({ change: null });
			onChanged({ agent: updated });
			return null;
		} catch (error) {
			return UtilityService.describeRequestError({ error, fallback: "That change did not save." });
		}
	};

	renderBase() {
		const { agent } = this.props;
		if (agent.maySeeBase) {
			return (
				<section className="agent-instructions__base tk-card">
					<div className="agent-instructions__base-head">
						<span className="tk-text-strong">Instructions the agent follows</span>
						<span className="tk-muted">Visible to its author and administrators — your reading of them is logged</span>
						<button type="button" className="tk-text-action agent-instructions__base-edit" onClick={() => this.onOpenChange({ kind: "base" })}>
							<Icon.Edit2 size={13} />
							Edit
						</button>
					</div>
					<div className="agent-instructions__base-text">{agent.baseInstructions}</div>
				</section>
			);
		}
		return null;
	}

	renderBrief() {
		const { agent } = this.props;
		if (!agent.brief) return null;
		const fields = PlaybookBuildService.briefFields().filter(field => agent.brief[field.key] && !["name", "description", "category"].includes(field.key));
		if (fields.length === 0) return null;
		return (
			<section className="agent-instructions__section">
				<span className="tk-text-strong">What it was built to do</span>
				<dl className="agent-instructions__brief tk-card">
					{fields.map(field => (
						<div key={field.key} className="agent-instructions__brief-row">
							<dt>{field.label}</dt>
							<dd>{agent.brief[field.key]}</dd>
						</div>
					))}
				</dl>
			</section>
		);
	}

	renderOutcome() {
		const { lastOutcome } = this.state;
		if (!lastOutcome) return null;
		const { review } = lastOutcome;
		const reviewedWith = review && review.reviewedWith ? ` by ${review.reviewedWith.skill} v${review.reviewedWith.version}` : "";
		if (lastOutcome.status === "applied") {
			return (
				<div className="tk-notice tk-notice--success">
					<div className="tk-notice__title">
						<Icon.CheckCircle size={14} />
						{review
							? `Added — judged ${PlaybookChangeRequestService.describeIntegrityVerdict({ verdict: review.verdict }).toLowerCase()}${reviewedWith}.`
							: "Added."}
					</div>
					{review && review.reason && <p className="tk-notice__detail">{review.reason}</p>}
				</div>
			);
		}
		return (
			<div className="tk-notice tk-notice--warning">
				<div className="tk-notice__title">
					<Icon.Clock size={14} />
					Sent to the playbook's creator for approval — {PlaybookChangeRequestService.describeIntegrityVerdict({ verdict: review.verdict }).toLowerCase()}
					{reviewedWith}.
				</div>
				<p className="tk-notice__detail">{review.reason}</p>
			</div>
		);
	}

	renderChangeRequest = request => {
		const { rejectingRequestId, rejectReason, isDeciding } = this.state;
		const isRejecting = rejectingRequestId === request.id;
		const status = PlaybookChangeRequestService.describeChangeRequestStatus({ status: request.status });
		return (
			<div key={request.id} className="agent-instructions__request">
				<div className="agent-instructions__request-head">
					<span className={`tk-badge tk-badge--${status.tone}`}>{status.label}</span>
					<span className="tk-muted">
						{request.kind === "addition_edited" ? "Change to an addition" : "New addition"} from {request.requestedBy} ·{" "}
						{moment(request.createdAt).format("MMM D")}
					</span>
				</div>
				<div className="agent-instructions__addition-text">{request.proposedText}</div>
				{request.review && (
					<div className="tk-muted">
						{PlaybookChangeRequestService.describeIntegrityVerdict({ verdict: request.review.verdict })}
						{request.review.reviewedWith && ` (${request.review.reviewedWith.skill} v${request.review.reviewedWith.version})`}:{" "}
						{request.review.reason}
					</div>
				)}
				{request.status !== "pending" && request.decidedBy && (
					<div className="tk-muted">
						{request.status === "withdrawn" ? "Withdrawn" : `${request.status === "approved" ? "Approved" : "Rejected"} by ${request.decidedBy}`} ·{" "}
						{moment(request.decidedAt).format("MMM D")}
						{request.decisionReason && ` — ${request.decisionReason}`}
					</div>
				)}
				{request.mayDecide && !isRejecting && (
					<div className="agent-instructions__request-actions">
						<button
							type="button"
							className="tk-button tk-button--secondary tk-button--compact"
							onClick={() => this.update({ rejectingRequestId: request.id, rejectReason: "" })}
							disabled={isDeciding}
						>
							Reject
						</button>
						<button
							type="button"
							className="tk-button tk-button--primary tk-button--compact"
							onClick={() => this.onDecideRequest({ requestId: request.id, isApproved: true })}
							disabled={isDeciding}
						>
							<Icon.Check size={13} />
							Approve
						</button>
					</div>
				)}
				{isRejecting && (
					<div className="agent-instructions__add-row">
						<input
							className="tk-input"
							value={rejectReason}
							onChange={event => this.update({ rejectReason: event.target.value })}
							placeholder={`Why — ${request.requestedBy} will see this`}
							autoFocus
						/>
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => this.update({ rejectingRequestId: null })}>
							Cancel
						</button>
						<button
							type="button"
							className="tk-button tk-button--primary tk-button--compact"
							onClick={() => this.onDecideRequest({ requestId: request.id, isApproved: false })}
							disabled={!rejectReason.trim() || isDeciding}
						>
							Reject change
						</button>
					</div>
				)}
				{request.isMine && request.status === "pending" && (
					<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => this.onWithdrawRequest({ requestId: request.id })}>
						Withdraw
					</button>
				)}
			</div>
		);
	};

	renderChangeRequests() {
		const { changeRequests, requestsError } = this.state;
		if (changeRequests.length === 0 && !requestsError) return null;
		const pendingCount = changeRequests.filter(request => request.status === "pending").length;
		return (
			<section className="agent-instructions__section">
				<div className="agent-instructions__section-head">
					<span className="tk-text-strong">Changes that needed approval</span>
					{pendingCount > 0 && <span className="tk-badge tk-badge--warning">{pendingCount} waiting</span>}
				</div>
				{requestsError && <div className="tk-notice tk-notice--danger">{requestsError}</div>}
				{changeRequests.length > 0 && <div className="tk-card">{changeRequests.map(this.renderChangeRequest)}</div>}
			</section>
		);
	}

	renderAddition = (addition, index) => {
		return (
			<div key={addition.id} className="agent-instructions__addition">
				<span className="agent-instructions__addition-number">{index + 1}</span>
				<div className="agent-instructions__addition-main">
					<div className="agent-instructions__addition-text">{addition.text}</div>
					<div className="tk-muted">
						Added by {addition.author} · {moment(addition.addedAt).format("MMM D, YYYY")}
						{addition.editedAt && ` · changed ${moment(addition.editedAt).format("MMM D")}`}
					</div>
				</div>
				{addition.isMine && (
					<span className="agent-instructions__addition-actions">
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => this.onOpenChange({ kind: "edit", addition })}>
							Edit
						</button>
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => this.onOpenChange({ kind: "remove", addition })}>
							Remove
						</button>
					</span>
				)}
			</div>
		);
	};

	renderAdditions() {
		const { agent } = this.props;
		return (
			<section className="agent-instructions__section">
				<div className="agent-instructions__section-head">
					<span className="tk-text-strong">Additions</span>
					<span className="tk-muted">Everyone can see these. Only the person who wrote one can change or remove it.</span>
				</div>
				{agent.additions.length === 0 ? (
					<div className="tk-empty tk-card">No additions yet.</div>
				) : (
					<div className="tk-card">{agent.additions.map(this.renderAddition)}</div>
				)}
			</section>
		);
	}

	renderAddForm() {
		const { draftText, draftNote, isAdding, addError } = this.state;
		const canAdd = draftText.trim().length > 0 && draftNote.trim().length > 0 && !isAdding;
		return (
			<section className="agent-instructions__add tk-card">
				<div className="tk-text-strong">Add an instruction</div>
				<textarea
					className="tk-input agent-instructions__add-text"
					rows={3}
					value={draftText}
					onChange={event => this.update({ draftText: event.target.value })}
					placeholder="e.g. For dental clinics, treat lab fees as cost of sales."
				/>
				<div className="agent-instructions__add-row">
					<input
						className="tk-input"
						value={draftNote}
						onChange={event => this.update({ draftNote: event.target.value })}
						placeholder="Why — shown in the version history"
						maxLength={300}
					/>
					<button type="button" className="tk-button tk-button--primary tk-button--compact" onClick={this.onAdd} disabled={!canAdd}>
						<Icon.Plus size={13} />
						{isAdding ? "Adding…" : "Add to the playbook"}
					</button>
				</div>
				<div className="tk-form-help">
					Additions change what the playbook says and looks at. They cannot change what it is allowed to do, or the facts its checks establish.
				</div>
				{addError && <div className="tk-notice tk-notice--danger agent-instructions__error">{addError}</div>}
			</section>
		);
	}

	renderChangeModal() {
		const { agent } = this.props;
		const { change } = this.state;
		if (!change) return null;
		const settingsByKind = {
			edit: { title: "Change your addition", textLabel: "Addition", initialText: change.addition && change.addition.text, confirmLabel: "Save change" },
			remove: {
				title: "Remove your addition",
				intro: change.addition && `"${change.addition.text}" will no longer be given to the agent. It stays in the version history.`,
				confirmLabel: "Remove addition",
				isDestructive: true,
			},
			base: {
				title: "Edit base instructions",
				textLabel: "Base instructions",
				initialText: agent.baseInstructions,
				textRows: 14,
				confirmLabel: "Save new version",
				textHelp: "Only you and administrators can see these.",
			},
		};
		const settings = settingsByKind[change.kind];
		return (
			<PlaybookChangeModal
				key={`${change.kind}-${change.addition ? change.addition.id : "base"}`}
				open={true}
				onClose={this.onCloseChange}
				onConfirm={this.onConfirmChange}
				{...settings}
			/>
		);
	}

	render() {
		return (
			<div className="agent-instructions">
				{this.renderBrief()}
				{this.renderBase()}
				{this.renderAdditions()}
				{this.renderOutcome()}
				{this.renderAddForm()}
				{this.renderChangeRequests()}
				{this.renderChangeModal()}
			</div>
		);
	}
}

export default PlaybookInstructionsPanel;
